import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '@/lib/api'
import { MAX_COMMENT_LENGTH, type CaseCommentEntry, type CaseDetail } from '@/lib/api-types'
import { useSessionStore } from '@/stores/session'
import { Button } from '@/components/ui/button'
import { Field, FieldHint, MicroLabel } from '@/components/ui/field'
import { Textarea } from '@/components/ui/textarea'

/** Counter appears once this few characters remain. */
const COUNTER_THRESHOLD = 500

let optimisticSeq = 0

/**
 * The reply box above a request's timeline. Sending appends the note
 * optimistically (synthetic id), then swaps in the canonical entry the
 * server minted; a failure rolls the timeline back and restores the text.
 * Cmd/Ctrl+Enter sends.
 */
export function CommentComposer({ caseId }: { caseId: string }) {
  const queryClient = useQueryClient()
  const contact = useSessionStore((s) => s.contact)
  const [text, setText] = useState('')
  const queryKey = ['case', caseId]

  const mutation = useMutation({
    mutationFn: (body: string) => api.postCaseComment(caseId, body),
    onMutate: async (body) => {
      // A racing 60s refetch must not clobber the optimistic row mid-flight.
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<CaseDetail>(queryKey)
      optimisticSeq += 1
      const optimistic: CaseCommentEntry = {
        kind: 'comment',
        id: `optimistic-${optimisticSeq}`,
        at: new Date().toISOString(),
        side: 'you',
        // Mirrors the server's attribution grammar ("{Name} ({Account})",
        // impersonated "{Actor} (Green Line) for {Name}") so the canonical
        // swap is invisible.
        authorName: contact
          ? contact.impersonated
            ? `${contact.actorName ?? 'Green Line'} (Green Line) for ${contact.firstName} ${contact.lastName}`
            : `${contact.firstName} ${contact.lastName} (${contact.accountName})`
          : 'You',
        body,
      }
      if (previous) {
        queryClient.setQueryData<CaseDetail>(queryKey, {
          ...previous,
          timeline: [optimistic, ...previous.timeline],
        })
      }
      return { previous, optimisticId: optimistic.id }
    },
    onSuccess: (entry, _body, context) => {
      // Swap the synthetic row for the canonical 201 entry. A refetch can
      // race the POST despite the cancelQueries above (one started mid-POST
      // lands after us in the cache): if it resolved WITHOUT the comment it
      // dropped the optimistic row — prepend the canonical entry so the sent
      // note doesn't vanish until the next poll; if it already carried the
      // canonical entry, just make sure the optimistic row is gone.
      queryClient.setQueryData<CaseDetail>(queryKey, (current) => {
        if (!current) return current
        const hasCanonical = current.timeline.some((row) => row.id === entry.id)
        const hasOptimistic = current.timeline.some((row) => row.id === context.optimisticId)
        if (hasOptimistic) {
          return {
            ...current,
            timeline: hasCanonical
              ? current.timeline.filter((row) => row.id !== context.optimisticId)
              : current.timeline.map((row) => (row.id === context.optimisticId ? entry : row)),
          }
        }
        return hasCanonical ? current : { ...current, timeline: [entry, ...current.timeline] }
      })
    },
    onError: (_error, body, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous)
      setText(body)
      toast.error('Your note didn’t send — it’s back in the box below.')
    },
  })

  function send() {
    const trimmed = text.trim()
    if (!trimmed || trimmed.length > MAX_COMMENT_LENGTH || mutation.isPending) return
    setText('')
    mutation.mutate(trimmed)
  }

  const remaining = MAX_COMMENT_LENGTH - text.length

  return (
    <div>
      <Field>
        <MicroLabel htmlFor="case-comment">Reply to Green Line</MicroLabel>
        <Textarea
          id="case-comment"
          rows={3}
          value={text}
          maxLength={MAX_COMMENT_LENGTH}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault()
              send()
            }
          }}
          placeholder="Add a note for your Green Line team…"
          aria-describedby="case-comment-hint"
        />
      </Field>
      <div className="mt-2 flex items-center justify-between gap-3">
        <FieldHint id="case-comment-hint">
          {remaining <= COUNTER_THRESHOLD ? (
            <span className={remaining <= 0 ? 'font-medium text-rust' : undefined}>
              {remaining} characters left
            </span>
          ) : (
            'Ctrl+Enter (⌘+Enter on Mac) sends.'
          )}
        </FieldHint>
        <Button
          type="button"
          size="sm"
          loading={mutation.isPending}
          disabled={text.trim() === ''}
          onClick={send}
        >
          Send
        </Button>
      </div>
    </div>
  )
}
